/**
 * MINI GERENCIAMENTO DE ALUNOS — regras puras (turmas, chamada e mensalidades).
 *
 * Espelha `backend/server.py` (`situacao_mensalidade`, `_frequencia_do_atleta`):
 *   * a SITUAÇÃO da mensalidade é DERIVADA (paga / pendente / atrasada) — nunca
 *     digitada, para não dessincronizar;
 *   * o percentual de frequência é presenças / aulas.
 *
 * Nada aqui decide quem está pronto para graduar nem toca no lado esportivo:
 * mensalidade atrasada NUNCA impede competição.
 */
import { hojeIso, dataIsoValida } from './graduacao';

export const SITUACOES = {
  PAGA: 'paga',
  PENDENTE: 'pendente',
  ATRASADA: 'atrasada',
};

export const ROTULO_SITUACAO = {
  paga: 'Pago',
  pendente: 'Pendente',
  atrasada: 'Atrasado',
};

export const CORES_SITUACAO = {
  paga: 'text-emerald-400',
  pendente: 'text-amber-400',
  atrasada: 'text-red-400',
};

/** Presença: 3 estados (decisão do usuário). */
export const STATUS_PRESENCA = [
  { value: 'present', label: 'Presente', cor: 'text-emerald-400' },
  { value: 'absent', label: 'Falta', cor: 'text-red-400' },
  { value: 'justified', label: 'Falta justificada', cor: 'text-amber-400' },
];

export const DIAS_SEMANA = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 7, label: 'Dom' },
];

/** "2026-09" → "Setembro/2026" (e "Set/2026" na versão curta). */
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export function competenciaTexto(competence, curto = false) {
  const texto = String(competence ?? '');
  if (!/^\d{4}-\d{2}$/.test(texto)) return '';
  const [ano, mes] = texto.split('-');
  const nome = MESES[Number(mes) - 1];
  if (!nome) return '';   // mês fora de 01..12 não vira "/2026"
  return `${curto ? nome.slice(0, 3) : nome}/${ano}`;
}

/** AAAA-MM de hoje (ou de uma data ISO). */
export function competenciaDe(iso) {
  return String(iso || hojeIso()).slice(0, 7);
}

/** Soma/ subtrai meses de uma competência (para navegar entre meses). */
export function somarMeses(competence, delta) {
  const texto = String(competence ?? '');
  if (!/^\d{4}-\d{2}$/.test(texto)) return competence;
  const total = Number(texto.slice(0, 4)) * 12 + (Number(texto.slice(5, 7)) - 1) + delta;
  const ano = Math.floor(total / 12);
  const mes = (total % 12) + 1;
  return `${ano}-${String(mes).padStart(2, '0')}`;
}

/**
 * SITUAÇÃO DERIVADA da mensalidade (a mesma regra do servidor):
 *   * tem pagamento        → paga;
 *   * venceu sem pagamento → atrasada;
 *   * ainda no prazo       → pendente.
 */
export function situacaoMensalidade(mensalidade, hoje = hojeIso()) {
  if (mensalidade?.paid_at) return SITUACOES.PAGA;
  const vencimento = String(mensalidade?.due_date || '').slice(0, 10);
  if (dataIsoValida(vencimento) && vencimento < hoje) return SITUACOES.ATRASADA;
  return SITUACOES.PENDENTE;
}

/** Rótulo e cor da situação (para a tabela e os selos). */
export function rotuloSituacao(situacao) {
  return ROTULO_SITUACAO[situacao] || situacao || '';
}

/** Percentual de frequência = presenças / aulas (null quando não há aulas). */
export function percentualFrequencia(frequencia) {
  const aulas = Number(frequencia?.aulas || 0);
  if (!aulas) return null;
  return Math.round((Number(frequencia?.presencas || 0) / aulas) * 100);
}

/** "3 aulas · 2 presenças · 1 falta · 67%" — texto curto para a tela. */
export function resumoFrequencia(frequencia) {
  const aulas = Number(frequencia?.aulas || 0);
  if (!aulas) return 'sem chamada registrada';
  const partes = [`${aulas} aula${aulas > 1 ? 's' : ''}`,
    `${Number(frequencia?.presencas || 0)} presença${Number(frequencia?.presencas || 0) === 1 ? '' : 's'}`];
  const faltas = Number(frequencia?.faltas || 0);
  const justificadas = Number(frequencia?.justificadas || 0);
  if (faltas) partes.push(`${faltas} falta${faltas > 1 ? 's' : ''}`);
  if (justificadas) partes.push(`${justificadas} justificada${justificadas > 1 ? 's' : ''}`);
  partes.push(`${percentualFrequencia(frequencia)}%`);
  return partes.join(' · ');
}

/** "R$ 150,00" (valor nulo vira "—", nunca "R$ 0"). */
export function formatarValor(valor) {
  if (valor === null || valor === undefined || valor === '') return '—';
  const numero = Number(valor);
  if (Number.isNaN(numero)) return '—';
  return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Horário da turma: "19:00 às 20:30" (ou só o que existir). */
export function horarioTurma(turma) {
  const inicio = turma?.start_time || '';
  const fim = turma?.end_time || '';
  if (inicio && fim) return `${inicio} às ${fim}`;
  return inicio || fim || '';
}

/** Dias da turma por extenso: "Seg, Qua e Sex". */
export function diasTurma(turma) {
  const mapa = Object.fromEntries(DIAS_SEMANA.map((d) => [d.value, d.label]));
  const dias = (turma?.weekdays || []).map((d) => mapa[d]).filter(Boolean);
  if (!dias.length) return '';
  if (dias.length === 1) return dias[0];
  return `${dias.slice(0, -1).join(', ')} e ${dias[dias.length - 1]}`;
}

/** O valor que será usado: do aluno > da turma (mesma ordem do servidor). */
export function valorDoAluno(turma, athleteId) {
  const aluno = (turma?.students || []).find((a) => a.athlete_id === athleteId);
  if (aluno && aluno.amount !== null && aluno.amount !== undefined) return Number(aluno.amount);
  if (turma?.default_amount !== null && turma?.default_amount !== undefined) return Number(turma.default_amount);
  return null;
}

/** Dia da semana (1=segunda ... 7=domingo) de uma data ISO, SEM fuso. */
export function diaDaSemana(iso) {
  const texto = String(iso ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) return null;
  const [ano, mes, dia] = texto.split('-').map(Number);
  const data = new Date(ano, mes - 1, dia);
  if (Number.isNaN(data.getTime())) return null;
  // o JS "rola" data impossível (31/02 vira 02/03): confere se voltou a mesma
  if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
    return null;
  }
  const js = data.getDay();               // 0=domingo ... 6=sábado
  return js === 0 ? 7 : js;
}

/**
 * Turma que a tela já abre selecionada (evita o professor escolher de novo):
 *   * uma turma só           → ela;
 *   * mais de uma            → a ÚNICA que tem aula hoje (pelos dias da turma);
 *   * nenhuma/ambíguo        → '' (fica como está, o usuário escolhe).
 * A escolha do usuário SEMPRE manda: isto só preenche campo vazio.
 */
export function turmaPadraoDoDia(turmas = [], hoje = hojeIso()) {
  const ativas = turmas.filter((t) => t && t.active !== false);
  const lista = ativas.length ? ativas : turmas.filter(Boolean);
  if (!lista.length) return '';
  if (lista.length === 1) return lista[0].id || '';
  const dia = diaDaSemana(hoje);
  if (!dia) return '';
  const comAulaHoje = lista.filter((t) => (t.weekdays || []).includes(dia));
  return comAulaHoje.length === 1 ? (comAulaHoje[0].id || '') : '';
}
